import React, { useState, useRef, useEffect } from "react";
import { Bell } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotifications, useMarkNotificationsRead } from "@/api/hooks";

const ICON_BY_TYPE = {
  task_pending: "\u23F3",
  task_approved: "\u2705",
  task_rejected: "\u274C",
  points_earned: "\u2B50",
  referral_earned: "\uD83D\uDD17",
};

function relativeHebrewTime(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "\u05E2\u05DB\u05E9\u05D9\u05D5";
  if (diffMin < 60) return `\u05DC\u05E4\u05E0\u05D9 ${diffMin} \u05D3\u05E7\u05D5\u05EA`;
  if (diffHr < 24) return `\u05DC\u05E4\u05E0\u05D9 ${diffHr} \u05E9\u05E2\u05D5\u05EA`;
  return `\u05DC\u05E4\u05E0\u05D9 ${diffDay} \u05D9\u05DE\u05D9\u05DD`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const prevUnreadRef = useRef(0);
  const qc = useQueryClient();

  const { data } = useNotifications();
  const markRead = useMarkNotificationsRead();

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  // Invalidate dashboard & me when unread count increases
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, qc]);

  // Click outside closes dropdown
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMarkAllRead = () => {
    markRead.mutate({});
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-8 h-8 flex items-center justify-center rounded-full transition-all hover:bg-white/10"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-black text-white px-1"
            style={{ background: "#ef4444" }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 rounded-2xl z-50 w-[340px]"
          style={{
            background: "#111",
            border: "1px solid #222",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid #222" }}
            dir="rtl"
          >
            <span className="text-sm font-black text-white">
              {"\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA"}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-bold transition-colors hover:opacity-80"
                style={{ color: "#c8f135" }}
              >
                {"\u05E1\u05DE\u05DF \u05D4\u05DB\u05DC \u05DB\u05E0\u05E7\u05E8\u05D0"}
              </button>
            )}
          </div>

          {/* Notification list */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "400px" }}
          >
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center" dir="rtl">
                <span
                  className="text-sm"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  {"\u05D0\u05D9\u05DF \u05D4\u05EA\u05E8\u05D0\u05D5\u05EA \u05D7\u05D3\u05E9\u05D5\u05EA"}
                </span>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="px-4 py-3 transition-colors hover:bg-white/5"
                  dir="rtl"
                  style={{
                    borderBottom: "1px solid #1a1a1a",
                    borderRight: n.read_at ? "none" : "3px solid #c8f135",
                  }}
                >
                  <div className="flex gap-3 items-start">
                    <span className="text-lg flex-shrink-0 mt-0.5">
                      {ICON_BY_TYPE[n.type] ?? "\uD83D\uDD14"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-white truncate">
                        {n.title}
                      </p>
                      <p
                        className="text-xs mt-0.5 line-clamp-2"
                        style={{ color: "rgba(255,255,255,0.5)" }}
                      >
                        {n.body}
                      </p>
                      <p
                        className="text-[10px] mt-1"
                        style={{ color: "rgba(255,255,255,0.3)" }}
                      >
                        {relativeHebrewTime(n.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
